<template>
	<table class="OrderItemsTable" data-cy="orderItemsTable">
		<thead>
			<tr>
				<th>{{ $t('text.serviceColumn') }}</th>
				<th>{{ $t('text.dateAndTime') }}</th>
				<th>{{ $t('text.name') }}</th>
				<th class="price-column">{{ $t('text.price') }}</th>
				<th class="action-column"></th>
			</tr>
		</thead>
		<tbody>
			<template v-for="row in rows" :key="row.item.orderItemId">
				<tr class="item-row"
					:class="{ 'not-rebookable': !row.item.rebookable, clickable: row.item.rebookable, affected: row.result === 'noAvailability', expanded: row.expanded }"
					:data-cy="`orderItemRow-${row.item.orderItemId}`"
					@click="onRowClick(row)"
				>
					<td>
						<div class="service-subtitle">{{ serviceSubtitle(row.item) }}</div>
						<div class="service-title">{{ row.item.name }}</div>
					</td>
					<td>{{ formatDateTime(row.item.consumptionDate, row.item.consumptionTime) }}</td>
					<td>{{ personName(row.item) }}</td>
					<td class="price-column">{{ formatPrice(row.item.price, row.item.currency) }}</td>
					<td class="action-column">
						<div class="action-cell">
							<v-tooltip v-if="isUpdated(row)" :text="updatedTooltip(row)" location="top">
								<template #activator="{ props }">
									<v-chip v-bind="props" size="small" color="#64c823" variant="flat" class="updated-badge" data-cy="updatedBadge">
										{{ $t('text.updatedBadge') }}
									</v-chip>
								</template>
							</v-tooltip>
							<v-tooltip v-if="row.item.rebookable && isDirty(row)" :text="$t('text.undoChanges')" location="top">
								<template #activator="{ props }">
									<v-btn v-bind="props" icon variant="text" size="small" :data-cy="`undo-${row.item.orderItemId}`" @click.stop="$emit('undo', row)">
										<v-icon>mdi-restore</v-icon>
									</v-btn>
								</template>
							</v-tooltip>
							<v-btn v-else-if="row.item.rebookable" icon variant="text" size="small" :data-cy="`edit-${row.item.orderItemId}`" @click.stop="$emit('toggle', row)">
								<v-icon>{{ row.expanded ? 'mdi-chevron-up' : 'mdi-pencil' }}</v-icon>
							</v-btn>
						</div>
					</td>
				</tr>
				<tr v-if="row.expanded" class="edit-row" :class="{ affected: row.result === 'noAvailability' }">
					<td colspan="5">
						<OrderItemEditForm
							:row="row"
							:min-date="minDate"
							@update:draft="$emit('update:draft', row, $event)"
							@update:month="$emit('update:month', row, $event)"
						/>
					</td>
				</tr>
			</template>
		</tbody>
	</table>
</template>

<script>
import OrderItemEditForm from './OrderItemEditForm.vue'
import { deepEqual, formatDateTime, formatPrice } from './rebooking-utils'

export default {
	name: 'OrderItemsTable',
	components: { OrderItemEditForm },
	props: {
		rows: { type: Array, default: () => [] },
		customer: Object,
		minDate: String,
	},
	methods: {
		formatDateTime,
		formatPrice,
		// whole row acts as a disclosure toggle (rebookable rows only); action buttons use
		// @click.stop so they don't double-toggle. Clicking the row never reverts edits — that
		// stays exclusive to the undo button.
		onRowClick(row) {
			if (row.item.rebookable) this.$emit('toggle', row)
		},
		isDirty(row) {
			return !deepEqual(row.draft, row.original)
		},
		// PEAK product_options.updated_info (persisted) or in-session submit result
		isUpdated(row) {
			return row.result === 'updated' || !!row.item.updatedInfo
		},
		updatedTooltip(row) {
			const info = row.item.updatedInfo
			const username = info?.adminUsername
			const userId = info?.adminUserId
			if (username && userId !== undefined && userId !== null) {
				return this.$t('text.rebookingTriggeredByWithId', { username, userId })
			}
			if (username) return this.$t('text.rebookingTriggeredBy', { username })
			if (userId !== undefined && userId !== null) return this.$t('text.rebookingTriggeredById', { userId })
			return this.$t('text.updatedBadge')
		},
		serviceSubtitle(item) {
			return [item.ticketCategory, item.ticketType].filter(Boolean).join(', ')
				+ (item.serviceProviderName ? ` | ${item.serviceProviderName}` : '')
		},
		personName(item) {
			const name = [item.personalization?.firstName, item.personalization?.lastName].filter(Boolean).join(' ')
			if (name) return name
			// 'Contact person only' items carry no per-item person on PEAK (event.personalization
			// is empty) — the relevant person is the order contact
			if (item.personalizationConfig?.personalizationType === 'Contact person only') {
				return [this.customer?.firstName, this.customer?.lastName].filter(Boolean).join(' ')
			}
			return ''
		},
	},
}
</script>

<style scoped>
.OrderItemsTable {
	width: 100%;
	border-collapse: collapse;
}
.OrderItemsTable th {
	text-align: left;
	font-size: 13px;
	font-weight: 600;
	padding: 8px 12px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.15);
}
.OrderItemsTable td {
	padding: 12px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.08);
	font-size: 14px;
	vertical-align: middle;
}
.service-subtitle { font-size: 11px; color: #8f8f8f; }
.service-title { font-size: 14px; }
/* th/td prefix needed: .OrderItemsTable th's text-align:left has equal specificity and would
   otherwise win by source order, leaving the header unaligned with the right-aligned cells */
th.price-column, td.price-column { text-align: right; white-space: nowrap; }
.action-column { width: 140px; text-align: right; }
.action-cell { display: inline-flex; align-items: center; justify-content: flex-end; gap: 4px; }
.item-row.clickable { cursor: pointer; }
.item-row.clickable:hover td { background: #f7fbfe; }
.item-row.clickable.expanded td { background: #f2f9fd; }
.item-row.not-rebookable td { color: #b5b5b5; }
.item-row.not-rebookable .service-title { color: #b5b5b5; }
/* affected item: a single red box around the header row AND its expanded edit form.
   Uses box-shadow (inset), NOT border: a real border on this row's top edge collapses with
   the previous row's bottom border, and which one wins is a table border-collapse conflict
   resolution that's too easy to lose (observed dropping the top edge in practice). box-shadow
   paints independently per cell regardless of neighbouring borders, so it can't be lost that way.
   Each rule below states the full set of edges for that cell/state combo (box-shadow
   declarations replace rather than merge across selectors). */
.item-row.affected:not(.expanded) td { box-shadow: inset 0 2px 0 0 #f34545, inset 0 -2px 0 0 #f34545; }
.item-row.affected:not(.expanded) td:first-child { box-shadow: inset 0 2px 0 0 #f34545, inset 0 -2px 0 0 #f34545, inset 2px 0 0 0 #f34545; }
.item-row.affected:not(.expanded) td:last-child { box-shadow: inset 0 2px 0 0 #f34545, inset 0 -2px 0 0 #f34545, inset -2px 0 0 0 #f34545; }
/* expanded: the box continues into the edit form, so no bottom edge on the header row here */
.item-row.affected.expanded td { box-shadow: inset 0 2px 0 0 #f34545; }
.item-row.affected.expanded td:first-child { box-shadow: inset 0 2px 0 0 #f34545, inset 2px 0 0 0 #f34545; }
.item-row.affected.expanded td:last-child { box-shadow: inset 0 2px 0 0 #f34545, inset -2px 0 0 0 #f34545; }
/* the box closes at the bottom of the edit form (a single colspan cell: left+right+bottom together) */
.edit-row.affected > td {
	box-shadow: inset 2px 0 0 0 #f34545, inset -2px 0 0 0 #f34545, inset 0 -2px 0 0 #f34545;
}
.edit-row > td { padding: 0; border-bottom: 1px solid rgba(0, 0, 0, 0.08); }
.updated-badge { color: white; }
</style>
